<template>
  <div class="h-100 w-100" :class="{ 'p-3': !hasFiles }">
    <input
      type="file"
      class="d-none"
      ref="fileInput"
      :id="fileInputId"
      :accept="acceptFiles"
      @change="onChangeFileInput"
      multiple
    />
    <label
      v-show="!hasFiles"
      :for="fileInputId"
      class="drop-target w-100 cursor-pointer border rounded h-100 p-2"
      :style="{ 'background-color': bgColor }"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <div class="drop-target-add-files h-100 rounded">
        <slot name="label">
          <div class="d-flex align-items-center justify-content-center">
            <div class="h1 pr-3">
              <i class="text-dark-50 flaticon-upload-1"></i>
            </div>
            <div>Drop files here or browse files</div>
          </div>
        </slot>
      </div>
    </label>

    <div
      v-if="title"
      v-show="hasFiles"
      class="kt-portlet__head align-items-center"
    >
      <div class="kt-portlet__head-label">
        <h3 class="kt-portlet__head-title">
          {{ title }}
        </h3>
      </div>
      <label
        v-if="canUploadFiles"
        :for="fileInputId"
        class="cursor-pointer"
        style="line-height: 0;"
      >
        <i class="text-dark-50 flaticon2-plus"></i> Add files
      </label>
    </div>
    <div
      v-show="hasFiles"
      :id="uppyDashboardUiId"
      :style="{ height: title ? dashboardHeight : '100% !important' }"
    ></div>
  </div>
</template>

<script>
import Uppy from "@uppy/core";
import XHRUpload from "@uppy/xhr-upload";
import Dashboard from "@uppy/dashboard";
import { v4 as uuidv4 } from "uuid";

export default {
  props: {
    title: {
      type: String,
      default: null,
    },
    url: {
      type: String,
      required: true,
    },
    maxFiles: {
      type: Number,
      default: null,
    },
    fileTypes: {
      type: Array,
      default: null,
    },
    meta: {
      type: Object,
      default: () => {
        return {};
      },
    },
    headers: {
      type: Object,
      default: () => {
        return {};
      },
    },
    bgColor: {
      type: String,
      default: "#FFFCF1",
    },
    dashboardHeight: {
      type: String,
      default: "100% !important",
    },
  },
  data() {
    return {
      uppy: null,
    };
  },
  computed: {
    uppyId() {
      return uuidv4();
    },
    fileInputId() {
      return `upload-Files-${this.uppyId}`;
    },
    uppyDashboardUiId() {
      return `uppy-dashboard-ui-${this.uppyId}`;
    },
    acceptFiles() {
      if (!this.fileTypes || this.fileTypes.length === 0) {
        return null;
      }
      return this.fileTypes.toString();
    },
    hasFiles() {
      if (!this.uppy) {
        return false;
      }
      return !!this.uppy.getFiles().length;
    },
    canUploadFiles() {
      if (!this.maxFiles || !this.hasFiles) {
        return true;
      }
      return this.uppy.getFiles().length < this.maxFiles;
    },
  },

  mounted() {
    this.uppy = new Uppy({
      id: this.uppyId,
      autoProceed: true,
      allowMultipleUploadBatches: true,
      restrictions: {
        maxNumberOfFiles: this.maxFiles,
        allowedFileTypes: this.fileTypes,
      },
      meta: this.meta,
    })
      .use(Dashboard, {
        inline: true,
        target: "#" + this.uppyDashboardUiId,
        hideUploadButton: false,
        browserBackButtonClose: false,
        showRemoveButtonAfterComplete: true,
        height: 630,
      })
      .use(XHRUpload, {
        endpoint: this.url,
        formData: true,
        fieldName: "file",
        headers: this.headers,
      })
      .on("file-added", function() {
        //  document.querySelector("#order-message-reply-btn").setAttribute("disabled","disabled");
      })
      .on("upload-success", (data, response) => {
        this.$emit("uploaded", data, response);
      })
      .on("file-removed", (file) => {
        this.$emit("removed", file);
      })
      .on("upload", () => {
        this.$emit("upload", true);
      })
      .on("complete", () => {
        this.$emit("complete", true);
      });
  },

  methods: {
    uploadFiles(files) {
      if (!this.uppy) {
        return;
      }
      files.forEach((file) => {
        try {
          this.uppy.addFile({
            source: "file input",
            name: file.name,
            type: file.type,
            data: file,
          });
          // eslint-disable-next-line no-empty
        } catch (err) {}
      });
    },

    onChangeFileInput(event) {
      const files = Array.from(event.target.files);
      this.uploadFiles(files);
      event.target.value = null;
    },

    drop(event) {
      event.preventDefault();
      this.uploadFiles(event.dataTransfer.files);

      event.currentTarget.classList.remove("drop-overlay");
    },
    dragover(event) {
      event.preventDefault();
      if (!event.currentTarget.classList.contains("drop-overlay")) {
        event.currentTarget.classList.add("drop-overlay");
      }
    },
    dragleave(event) {
      event.currentTarget.classList.remove("drop-overlay");
    },
  },
};
</script>

<style lang="css">
@import "https://releases.transloadit.com/uppy/v3.1.1/uppy.min.css";

.cursor-pointer {
  cursor: pointer;
}
.drop-target .drop-target-add-files {
  border: 2px dashed #dfdfdf;
}
.drop-overlay {
  background-color: #e5e7eb !important;
  border: 3px dashed #6b7280;
}

.uppy-Root,
.uppy-Dashboard,
/* .uppy-Dashboard-inner, */
.uppy-Dashboard-innerWrap,
.uppy-Dashboard-files {
  height: 100% !important;
  width: 100% !important;
}
.uppy-Dashboard-files > div {
  height: 70% !important;
}

.uppy-DashboardContent-bar {
  display: none !important;
}

.uppy-size--md .uppy-Dashboard-inner {
  min-height: auto;
  min-width: 100%;
}

.uppy-Dashboard-AddFiles-list,
.uppy-StatusBar-actions .uppy-StatusBar-actionBtn--done {
  display: none !important;
}

[dir="ltr"] .uppy-size--md .uppy-Dashboard-Item-action--remove {
  right: -6px !important;
  top: -9px !important;
}

.uppy-StatusBar,
.uppy-Dashboard-Item-previewInnerWrap:after,
.uppy-DashboardContent-bar,
.uppy-size--md .uppy-Dashboard-Item-action--remove,
.uppy-Dashboard-Item-progress {
  z-index: 50 !important;
}
</style>
